// The state for PacePro creation
import {
    SET_PACEPRO_CREATE_COURSE,
    SET_PACEPRO_CREATE_DISTANCE,
    GET_PACEPRO_CREATE,
    RESET_PACEPRO_CREATE,
    SET_PACEPRO_CREATE_STRATEGY,
    SET_PACEPRO_CREATE_HILL_EFFORT,
    SET_PACEPRO_CREATE_ELEVATION_SEGMENT_MIN_LENGTH,
    SET_PACEPRO_CREATE_SPLITS,
    SET_PACEPRO_CREATE_GOALTIME,
    SET_PACEPRO_CREATE_PACE_INTERVALS,
    SET_PACEPRO_CREATE_NAME,
    SET_PACEPRO_CREATE,
    RESET_PACEPRO_CREATE_STRATEGY,
    RESET_PACEPRO_CREATE_HILL_EFFORT,
    RESET_PACEPRO_CREATE_ELEVATION_SEGMENT_MIN_LENGTH,
    INITIALIZE_PACEPRO_CREATE,
    SET_PACEPRO_CREATE_MANUAL_PACE,
    SET_PACEPRO_CREATE_COURSE_FROM_EVENT
} from 'actions/ActionTypes';

export const SPLIT_TYPES = {
    MILE: 'DISTANCE_MILE',
    KILOMETER: 'DISTANCE_KILOMETER',
    ELEVATION: 'ELEVATION'
};

export const DISTANCES = {
    '5K': '5K',
    '10K': '10K',
    HALF_MARATHON: 'HALF_MARATHON',
    MARATHON: 'MARATHON',
    OTHER: 'OTHER',
    COURSE: 'COURSE'
};

export const DISTANCES_METERS = {
    '5K': 5000,
    '10K': 10000,
    HALF_MARATHON: 21097.5,
    MARATHON: 42195,
    OTHER: 10000,
    COURSE: 0
};

export const DISTANCES_DEFAULT_TIME_SECONDS = {
    '5K': 1500,
    '10K': 3000,
    HALF_MARATHON: 7200,
    MARATHON: 14400
};

export const SAVE_STATES = {
    SAVE_READY: 'SAVE_READY',
    SAVED: 'SAVED'
};

export const DISTANCE_UNITS = {
    MI: 'MILE',
    KM: 'KILOMETER'
};

export const PACE_TIMES = {
    METRIC_FOUR_MIN_PACE: 148, // 4 min/mi in min/km as seconds
    METRIC_TWENTY_MIN_PACE: 745.645431, // 20 min/mi in min/km as seconds
    STATUTE_FOUR_MIN_PACE: 240, // 4 min/mi as seconds
    STATUTE_TWENTY_MIN_PACE: 1200 // 20 min/mi as seconds
};

export const MAX_GOAL_TIMES = {
    HOURS: 50 // Maximum goal time of 50 hours
};

export const DEFAULT_STATE = {
    distance: DISTANCES['10K'], // distance Type
    customDistance: DISTANCES_METERS['10K'], // total distance
    customDistanceUnit: null, // default to null, only use this field when doing a custom distance...
    course: null,
    goalTime: 3000,
    splits: SPLIT_TYPES.KILOMETER, // split type
    pacingStrategy: 0, // pacingStrategy
    hillEffort: 0, // uphill effort
    elevationSegmentMinLength: 1700,
    paceIntervals: [],
    id: null, // null is default (unloaded) state, set to 0 once a course / pacepro is loaded or a pacepro creation flow is started
    saveState: SAVE_STATES.SAVE_READY,
    name: '',
    manualPaceEdit: false,
    source: null
};

/**
 * example paceInterval
 *
 *  {
 *      bearing: null
 *      cumulativeDistanceFromStart: 1609.343994140625
 *      cumulativeTimeFromStart: 482.803
 *      endPoint: {latitude: null, longitude: null, elevation: null}
 *      paceBandPk: 1625
 *      paceBandSplitPk: 19161
 *      splitAvgSpeed: 3.3333331647756177
 *      splitDistance: 1609.343994140625
 *      startPoint: {latitude: null, longitude: null, elevation: null}
 *  }
 *
 */

export const DEFAULT_PACE = 0.335540444; // 9 min/mi in s/m

export const PACING_STRATEGY_RANGE = {
    MIN: -5,
    MAX: 5
};

export const HILL_EFFORT_RANGE = {
    MIN: -2.5,
    MAX: 2.5
};

export const ELEVATION_SEGMENT_MIN_LENGTH_RANGE = {
    MIN: 400, // MORE splits
    MAX: 3000 // FEWER splits
};

export const roundGoalTime = (number) => {
    return Math.ceil(number); // requirement is to round up
};

// Initial state of the PacePro, used to compare for save state
let initialState = { ...DEFAULT_STATE };

/**
 *
 * Initialize the PacePro, this initial state should be what is on the server, before edits occur locally
 * New PacePros are not initialized as they have not been persisted to the server yet.
 * Only use this when getting a pacepro from the server, this applies to duplicate also (as the data is from the server)
 *
 */
export const initializePacePro = (state, action) => {
    const paceProCreate = action.paceProCreate;

    const defaultKeys = Object.keys(DEFAULT_STATE);

    let newState = {};

    // iterate through the default state keys
    // add changed values, this ensures new unsupported fields are not introduced
    for (let key of defaultKeys) {
        if (paceProCreate[key] !== undefined) {
            if (key === 'customDistanceUnit' && paceProCreate[key] === null) {
                continue; // If the distance unit is null (which is possible from the API), we dont want to use it. We will persist the unit when the users saves it next time.
            } else {
                newState[key] = paceProCreate[key];
            }
        }
    }

    let pacePro = {
        ...state,
        ...newState
    };

    initialState = {
        ...pacePro
    };

    return pacePro;
};

const determineSaveState = (state) => {
    if (!state.id) {
        return SAVE_STATES.SAVE_READY;
    }

    const paceProKeys = Object.keys(DEFAULT_STATE);

    for (let key of paceProKeys) {
        if (
            key !== 'saveState' && // dont check saveState to determine saveState...
            key !== 'paceIntervals' && // paceIntervals are a special case
            state[key] !== initialState[key]
        ) {
            return SAVE_STATES.SAVE_READY;
        }
    }

    if (
        state.manualPaceEdit && // only need to check th epaceIntervals when they are manually editted
        !manualPaceIntervalsAreEqual(state.paceIntervals, initialState.paceIntervals)
    ) {
        return SAVE_STATES.SAVE_READY;
    }

    return SAVE_STATES.SAVED;
};

const manualPaceIntervalsAreEqual = (paceIntervals1, paceIntervals2) => {
    // for manual we are only changing the pace time, check if they are equal to one second.

    if (paceIntervals1.length !== paceIntervals2.length) {
        return false;
    }

    for (let i = 0; i < paceIntervals1.length; i++) {
        const paceInterval1 = paceIntervals1[0];
        const paceInterval2 = paceIntervals2[0];

        const paceTime1 = Math.round(1 / (paceInterval1.splitAvgSpeed / paceInterval1.splitDistance));
        const paceTime2 = Math.round(1 / (paceInterval2.splitAvgSpeed / paceInterval2.splitDistance));

        if (paceTime1 !== paceTime2) {
            return false;
        }
    }

    return true;
};

export const setDistance = (state, action) => {
    /*
        {
            distance: [DISTANCE_KEY], - required
            customDistance: float - optional
        }
    */
    const distanceKeys = Object.keys(DISTANCES);
    const distance = action.paceProCreate.distance;
    if (distanceKeys.indexOf(distance) === -1) {
        // invalid distance key, don't do anything...
        return state;
    }

    let customDistance = state.customDistance;
    let goalTime = state.goalTime;

    if (distance === DISTANCES.OTHER) {
        if (action.paceProCreate.customDistance === 0 || action.paceProCreate.customDistance) {
            customDistance = parseFloat(action.paceProCreate.customDistance);
        }
        goalTime = customDistance * DEFAULT_PACE; // 9 min/mi in s/m
    } else {
        customDistance = DISTANCES_METERS[action.paceProCreate.distance];
        goalTime = DISTANCES_DEFAULT_TIME_SECONDS[action.paceProCreate.distance];
    }

    let customDistanceUnit = state.customDistanceUnit;
    if (action.paceProCreate.customDistanceUnit) {
        customDistanceUnit = action.paceProCreate.customDistanceUnit;
    }

    const newState = {
        ...state,
        distance: distance,
        customDistance: customDistance,
        customDistanceUnit: customDistanceUnit,
        course: null,
        goalTime: roundGoalTime(goalTime),
        id: state.id === null ? 0 : state.id // if the id is null, set it to 0 since edits have begun, otherwise use the existing id
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const setCourseFromEvent = (state, action) => {
    const newState = {
        ...state,
        course: action.paceProCreate.course,
        distance: DISTANCES.OTHER,
        customDistance: action.paceProCreate.customDistance,
        eventDistance: action.paceProCreate.eventDistance,
        goalTime: action.paceProCreate.customTime || roundGoalTime(action.paceProCreate.customDistance * DEFAULT_PACE), // 9 min/mi in s/m
        splits: action.paceProCreate.splits,
        name: action.paceProCreate.name,
        id: 0,
        customTime: action.paceProCreate.customTime,
        eventId: action.paceProCreate.eventId,
        shareableEventUuid: action.paceProCreate.shareableEventUuid,
        eventDate: action.paceProCreate.eventDate
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const setCourse = (state, action) => {
    const newState = {
        ...state,
        course: action.paceProCreate.course,
        distance: DISTANCES.COURSE,
        customDistance: action.paceProCreate.customDistance,
        goalTime: roundGoalTime(action.paceProCreate.customDistance * DEFAULT_PACE), // 9 min/mi in s/m
        splits: action.paceProCreate.splits,
        name: action.paceProCreate.name,
        id: 0
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const setPacingStrategy = (state, action) => {
    const pacingStrategy = parseFloat(action.paceProCreate.pacingStrategy, 10) || 0;
    // if the value is invalid, do not change the state
    if (
        (pacingStrategy || pacingStrategy === 0) &&
        pacingStrategy < PACING_STRATEGY_RANGE.MIN &&
        pacingStrategy > PACING_STRATEGY_RANGE.MAX
    ) {
        return state;
    }

    const newState = {
        ...state,
        pacingStrategy: pacingStrategy
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const setHillEffort = (state, action) => {
    const hillEffort = parseFloat(action.paceProCreate.hillEffort, 10) || 0;
    // if the value is invalid, do not change the state
    if ((hillEffort || hillEffort === 0) && hillEffort < HILL_EFFORT_RANGE.MIN && hillEffort > HILL_EFFORT_RANGE.MAX) {
        return state;
    }

    const newState = {
        ...state,
        hillEffort: hillEffort
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const setElevationSegmentMinLength = (state, action) => {
    const elevationSegmentMinLength = parseFloat(action.paceProCreate.elevationSegmentMinLength, 10) || 0;
    // if the value is invalid, do not change the state
    if (
        (elevationSegmentMinLength || elevationSegmentMinLength === 0) &&
        elevationSegmentMinLength < ELEVATION_SEGMENT_MIN_LENGTH_RANGE.MIN &&
        elevationSegmentMinLength > ELEVATION_SEGMENT_MIN_LENGTH_RANGE.MAX
    ) {
        return state;
    }

    const newState = {
        ...state,
        elevationSegmentMinLength: elevationSegmentMinLength
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const setGoalTime = (state, action) => {
    const newState = {
        ...state,
        goalTime: roundGoalTime(action.paceProCreate.goalTime)
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const setSplits = (state, action) => {
    const splitsValues = Object.values(SPLIT_TYPES);
    const splits = action.paceProCreate.splits;
    if (splitsValues.indexOf(splits) === -1) {
        // invalid splits value, don't do anything...
        return state;
    }

    const newState = {
        ...state,
        splits: action.paceProCreate.splits
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const setPaceIntervals = (state, action) => {
    const newState = {
        ...state,
        paceIntervals: action.paceProCreate.paceIntervals
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const resetPacePro = (state, action) => {
    initialState = { ...DEFAULT_STATE };
    return DEFAULT_STATE;
};

export const resetPacingStrategy = (state, action) => {
    const newState = {
        ...state,
        pacingStrategy: DEFAULT_STATE.pacingStrategy
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const resetHillEffort = (state, action) => {
    const newState = {
        ...state,
        hillEffort: DEFAULT_STATE.hillEffort
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const resetElevationSegmentMinLength = (state, action) => {
    const newState = {
        ...state,
        elevationSegmentMinLength: DEFAULT_STATE.elevationSegmentMinLength
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const getPacePro = (state) => {
    return state.paceProCreate;
};

export const setPacePro = (state, action) => {
    const newState = {
        ...state,
        saveState: SAVE_STATES.SAVED,
        ...((action.paceProCreate.id || action.paceProCreate.id === 0) && { id: action.paceProCreate.id }),
        ...(action.paceProCreate.name && { name: action.paceProCreate.name }),
        ...(action.paceProCreate.splits && { splits: action.paceProCreate.splits }),
        ...(action.paceProCreate.goalTime && { goalTime: roundGoalTime(action.paceProCreate.goalTime) }),
        ...((action.paceProCreate.hillEffort || action.paceProCreate.hillEffort === 0) && {
            hillEffort: parseFloat(action.paceProCreate.hillEffort, 10)
        }),
        ...((action.paceProCreate.pacingStrategy || action.paceProCreate.pacingStrategy === 0) && {
            pacingStrategy: parseFloat(action.paceProCreate.pacingStrategy, 10)
        }),
        ...((action.paceProCreate.elevationSegmentMinLength ||
            action.paceProCreate.elevationSegmentMinLength === 0) && {
            elevationSegmentMinLength: parseFloat(action.paceProCreate.elevationSegmentMinLength, 10)
        }),
        ...(action.paceProCreate.customDistance && { customDistance: action.paceProCreate.customDistance }),
        ...(action.paceProCreate.customDistanceUnit && { customDistanceUnit: action.paceProCreate.customDistanceUnit }),
        ...(action.paceProCreate.course && { course: action.paceProCreate.course }),
        ...(action.paceProCreate.distance && { distance: action.paceProCreate.distance }),
        ...(action.paceProCreate.manualPaceEdit !== undefined && {
            manualPaceEdit: action.paceProCreate.manualPaceEdit
        })
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const setName = (state, action) => {
    const newState = {
        ...state,
        name: action.paceProCreate.name
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

export const setManualPaceEdit = (state, action) => {
    const newState = {
        ...state,
        manualPaceEdit: action.paceProCreate.manualPaceEdit
    };

    return {
        ...newState,
        saveState: determineSaveState(newState)
    };
};

const handlers = {
    [SET_PACEPRO_CREATE_COURSE]: setCourse,
    [SET_PACEPRO_CREATE_COURSE_FROM_EVENT]: setCourseFromEvent,
    [SET_PACEPRO_CREATE_DISTANCE]: setDistance,
    [GET_PACEPRO_CREATE]: getPacePro,
    [RESET_PACEPRO_CREATE]: resetPacePro,
    [RESET_PACEPRO_CREATE_STRATEGY]: resetPacingStrategy,
    [RESET_PACEPRO_CREATE_HILL_EFFORT]: resetHillEffort,
    [RESET_PACEPRO_CREATE_ELEVATION_SEGMENT_MIN_LENGTH]: resetElevationSegmentMinLength,
    [SET_PACEPRO_CREATE_STRATEGY]: setPacingStrategy,
    [SET_PACEPRO_CREATE_HILL_EFFORT]: setHillEffort,
    [SET_PACEPRO_CREATE_ELEVATION_SEGMENT_MIN_LENGTH]: setElevationSegmentMinLength,
    [SET_PACEPRO_CREATE_SPLITS]: setSplits,
    [SET_PACEPRO_CREATE_GOALTIME]: setGoalTime,
    [SET_PACEPRO_CREATE_PACE_INTERVALS]: setPaceIntervals,
    [SET_PACEPRO_CREATE_NAME]: setName,
    [SET_PACEPRO_CREATE]: setPacePro,
    [INITIALIZE_PACEPRO_CREATE]: initializePacePro,
    [SET_PACEPRO_CREATE_MANUAL_PACE]: setManualPaceEdit
};

export default function paceProCreate(state = DEFAULT_STATE, action = {}) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}
